import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CheckSquare, FileText, CalendarDays } from "lucide-react";
import { UnifiedApprovalQueue } from "./content/UnifiedApprovalQueue";
import { BlogPostsPanel } from "./content/BlogPostsPanel";
import { ContentCalendarPanel } from "./content/ContentCalendarPanel";

type ContentTab = "approval" | "posts" | "calendar";

const sans = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const T = {
  navy:   "#002868",
  red:    "#B22234",
  gold:   "#C5A55A",
  border: "rgba(27,27,27,0.12)",
};

export function ContentManager() {
  const [activeTab, setActiveTab] = useState<ContentTab>("approval");

  // Live pending count badge
  const { data: pendingCount = 0 } = useQuery({
    queryKey: ["content_pending_count"],
    queryFn: async () => {
      const { count: blogCount } = await supabase
        .from("blog_posts" as any)
        .select("id", { count: "exact", head: true })
        .eq("status", "pending_approval");

      const { count: socialCount } = await supabase
        .from("social_posts" as any)
        .select("id", { count: "exact", head: true })
        .eq("status", "pending_approval");

      return (blogCount ?? 0) + (socialCount ?? 0);
    },
    refetchInterval: 30_000,
  });

  const TABS: {
    id: ContentTab;
    label: string;
    icon: React.ReactNode;
    badge?: number;
  }[] = [
    {
      id: "approval",
      label: "Approval Queue",
      icon: <CheckSquare size={14} />,
      badge: pendingCount > 0 ? pendingCount : undefined,
    },
    { id: "posts",    label: "Blog Posts",       icon: <FileText     size={14} /> },
    { id: "calendar", label: "Content Calendar", icon: <CalendarDays size={14} /> },
  ];

  return (
    <div>
      {/* Inner tab bar — matches SocialMediaManager exactly */}
      <div style={{
        display: "flex",
        gap: 0,
        borderBottom: `1px solid ${T.border}`,
        marginBottom: 24,
        backgroundColor: "rgba(255,255,255,0.7)",
        borderRadius: "8px 8px 0 0",
        padding: "0 4px",
        border: `1px solid ${T.border}`,
        borderBottomColor: T.border,
      }}>
        {TABS.map(tab => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "11px 18px",
                border: "none",
                borderBottom: active ? `2px solid ${T.red}` : "2px solid transparent",
                backgroundColor: active ? "rgba(178,34,52,0.05)" : "transparent",
                color: active ? T.navy : "rgba(27,27,27,0.62)",
                fontSize: 13,
                fontFamily: sans,
                fontWeight: active ? 700 : 500,
                cursor: "pointer",
                transition: "all 0.15s ease",
                whiteSpace: "nowrap",
                position: "relative",
              }}
            >
              <span style={{ opacity: active ? 1 : 0.6 }}>{tab.icon}</span>
              {tab.label}
              {tab.badge !== undefined && (
                <span style={{
                  backgroundColor: T.red,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  padding: "1px 6px",
                  borderRadius: 99,
                  marginLeft: 4,
                  lineHeight: "16px",
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab panels */}
      {activeTab === "approval" && <UnifiedApprovalQueue />}
      {activeTab === "posts" && <BlogPostsPanel />}
      {activeTab === "calendar" && <ContentCalendarPanel />}
    </div>
  );
}
